import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

// ============ LIST CUSTOM FEES ============
const requested = createAction("customFees/requested")
const received = createAction("customFees/received")
const failed = createAction("customFees/failed")

// ============ GET SINGLE CUSTOM FEE ============
const requestedCustomFee = createAction("customFees/requestedCustomFee")
const receivedCustomFee = createAction("customFees/receivedCustomFee")
const failedCustomFee = createAction("customFees/failedCustomFee")

// ============ CREATE CUSTOM FEE ============
const requestedCreateCustomFee = createAction("customFees/requestedCreateCustomFee")
const receivedCreateCustomFee = createAction("customFees/receivedCreateCustomFee")
const failedCreateCustomFee = createAction("customFees/failedCreateCustomFee")

// ============ UPDATE CUSTOM FEE ============
const requestedUpdateCustomFee = createAction("customFees/requestedUpdateCustomFee")
const receivedUpdateCustomFee = createAction("customFees/receivedUpdateCustomFee")
const failedUpdateCustomFee = createAction("customFees/failedUpdateCustomFee")

// ============ DELETE CUSTOM FEE ============
const requestedDeleteCustomFee = createAction("customFees/requestedDeleteCustomFee")
const receivedDeleteCustomFee = createAction("customFees/receivedDeleteCustomFee")
const failedDeleteCustomFee = createAction("customFees/failedDeleteCustomFee")

// ============ GET ASSOCIATED EXPERIENCES ============
const requestedAssociatedExperiences = createAction("customFees/requestedAssociatedExperiences")
const receivedAssociatedExperiences = createAction("customFees/receivedAssociatedExperiences")
const failedAssociatedExperiences = createAction("customFees/failedAssociatedExperiences")

// ============ TOGGLE LINE ITEM ============
const requestedToggleLineItem = createAction("customFees/requestedToggleLineItem")
const receivedToggleLineItem = createAction("customFees/receivedToggleLineItem")
const failedToggleLineItem = createAction("customFees/failedToggleLineItem")

// ============ INITIAL STATE ============
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: PAGINATION_INITIAL_STATE,
  customFee: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  createCustomFee: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  updateCustomFee: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  deleteCustomFee: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  associatedExperiences: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  toggleLineItem: {
    ...LOADING_INITIAL_STATE,
    data: null
  }
}

// ============ REDUCER ============
const customFeesReducer = createReducer(initialState, {
  // List custom fees
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.items = payload.data || payload
    if (payload.metadata) {
      Object.assign(state.metadata, payload.metadata)
    }
  },
  [failed.type]: (state) => {
    setFailed(state)
  },

  // Get single custom fee
  [requestedCustomFee.type]: (state) => {
    setRequested(state.customFee)
  },
  [receivedCustomFee.type]: (state, { payload }) => {
    setReceived(state.customFee)
    state.customFee.data = payload.data || payload
  },
  [failedCustomFee.type]: (state) => {
    setFailed(state.customFee)
  },

  // Create custom fee
  [requestedCreateCustomFee.type]: (state) => {
    setRequested(state.createCustomFee)
  },
  [receivedCreateCustomFee.type]: (state) => {
    setReceived(state.createCustomFee)
  },
  [failedCreateCustomFee.type]: (state) => {
    setFailed(state.createCustomFee)
  },

  // Update custom fee
  [requestedUpdateCustomFee.type]: (state) => {
    setRequested(state.updateCustomFee)
  },
  [receivedUpdateCustomFee.type]: (state) => {
    setReceived(state.updateCustomFee)
  },
  [failedUpdateCustomFee.type]: (state) => {
    setFailed(state.updateCustomFee)
  },

  // Delete custom fee
  [requestedDeleteCustomFee.type]: (state) => {
    setRequested(state.deleteCustomFee)
  },
  [receivedDeleteCustomFee.type]: (state) => {
    setReceived(state.deleteCustomFee)
  },
  [failedDeleteCustomFee.type]: (state) => {
    setFailed(state.deleteCustomFee)
  },

  // Get associated experiences
  [requestedAssociatedExperiences.type]: (state) => {
    setRequested(state.associatedExperiences)
    state.associatedExperiences.data = []
  },
  [receivedAssociatedExperiences.type]: (state, { payload }) => {
    setReceived(state.associatedExperiences)
    state.associatedExperiences.data = payload.data || payload
  },
  [failedAssociatedExperiences.type]: (state) => {
    setFailed(state.associatedExperiences)
  },

  // Toggle line item
  [requestedToggleLineItem.type]: (state) => {
    setRequested(state.toggleLineItem)
  },
  [receivedToggleLineItem.type]: (state) => {
    setReceived(state.toggleLineItem)
  },
  [failedToggleLineItem.type]: (state) => {
    setFailed(state.toggleLineItem)
  }
})

export default customFeesReducer

// ============ API ACTIONS ============

/**
 * Get all custom fees
 * @param {Object} params - Query parameters
 */
export const getCustomFees = (params = {}) =>
  api({
    url: "/guide/settings/custom_fees",
    method: "get",
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

/**
 * Get single custom fee by ID
 * @param {Number} id - Custom fee ID
 */
export const getCustomFee = (id) =>
  api({
    url: `/guide/settings/custom_fees/${id}`,
    method: "get",
    onStart: requestedCustomFee.type,
    onSuccess: receivedCustomFee.type,
    onError: failedCustomFee.type
  })

/**
 * Create a new custom fee
 * @param {Object} data - Custom fee data
 * @param {String} data.title - Fee title
 * @param {Number} data.price - Fee price
 * @param {String} data.status - Fee status ("active" or "inactive")
 */
export const createCustomFee = (data) =>
  api({
    url: "/guide/settings/custom_fees",
    method: "post",
    data: { custom_fee: data },
    onStart: requestedCreateCustomFee.type,
    onSuccess: receivedCreateCustomFee.type,
    onError: failedCreateCustomFee.type,
    showSuccessNotification: true
  })

/**
 * Update an existing custom fee
 * @param {Number} id - Custom fee ID
 * @param {Object} data - Custom fee data to update
 */
export const updateCustomFee = (id, data) =>
  api({
    url: `/guide/settings/custom_fees/${id}`,
    method: "put",
    data: { custom_fee: data },
    onStart: requestedUpdateCustomFee.type,
    onSuccess: receivedUpdateCustomFee.type,
    onError: failedUpdateCustomFee.type,
    showSuccessNotification: true
  })

/**
 * Delete a custom fee
 * @param {Number} id - Custom fee ID
 */
export const deleteCustomFee = (id) =>
  api({
    url: `/guide/settings/custom_fees/${id}`,
    method: "delete",
    onStart: requestedDeleteCustomFee.type,
    onSuccess: receivedDeleteCustomFee.type,
    onError: failedDeleteCustomFee.type,
    props: { id },
    showSuccessNotification: true
  })

/**
 * Get trips associated with a custom fee
 * @param {Number} id - Custom fee ID
 */
export const getAssociatedExperiences = (id) =>
  api({
    url: `/guide/settings/custom_fees/${id}/associated_trips`,
    method: "get",
    onStart: requestedAssociatedExperiences.type,
    onSuccess: receivedAssociatedExperiences.type,
    onError: failedAssociatedExperiences.type
  })

/**
 * Toggle line item display for a custom fee
 * @param {Number} id - Custom fee ID
 * @param {Boolean} showAsLineItem - Whether to show as line item
 */
export const toggleLineItem = (id, showAsLineItem) =>
  api({
    url: `/guide/settings/custom_fees/${id}`,
    method: "patch",
    data: { custom_fee: { show_as_line_item: showAsLineItem } },
    onStart: requestedToggleLineItem.type,
    onSuccess: receivedToggleLineItem.type,
    onError: failedToggleLineItem.type,
    showSuccessNotification: true
  })
